/* ==========================================================================
   THE ETERNAL CLOCK
   Counts down a rolling cycle. When it reaches nothing, it begins again.
   It never stops. Vanilla JS, zero dependencies.
   ========================================================================== */
(function () {
  'use strict';

  if (window.KryptikEternalClock) {
    window.KryptikEternalClock.scan(document);
    return;
  }

  var DAY = 86400000;
  var DEFAULT_ANCHOR = Date.UTC(2026, 0, 1, 0, 0, 0);
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function Clock(el) {
    this.el = el;
    var days = parseInt(el.getAttribute('data-cycle-days'), 10);
    this.cycle = (isFinite(days) && days > 0 ? days : 13) * DAY;
    var anchor = Date.parse(el.getAttribute('data-anchor') || '');
    this.anchor = isFinite(anchor) ? anchor : DEFAULT_ANCHOR;
    this.units = {
      d: el.querySelector('[data-unit="d"]'),
      h: el.querySelector('[data-unit="h"]'),
      m: el.querySelector('[data-unit="m"]'),
      s: el.querySelector('[data-unit="s"]')
    };
    this.readout = el.querySelector('[data-clock-readout]');
    this.face = el.querySelector('[data-clock-face]');
    this.prevRemaining = Infinity;
    this.lastMinute = -1;
    this.timer = null;
    this.tick = this.tick.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.tick();
    if (!reduceMotion && el.getAttribute('data-embers') === 'true') this.summonEmbers();
  }

  Clock.prototype.remaining = function () {
    var elapsed = (Date.now() - this.anchor) % this.cycle;
    if (elapsed < 0) elapsed += this.cycle;
    return this.cycle - elapsed;
  };

  Clock.prototype.setUnit = function (node, value) {
    if (!node) return;
    var text = pad(value);
    var digits = node.children;
    for (var i = 0; i < text.length && i < digits.length; i++) {
      var digit = digits[i];
      if (digit.textContent !== text.charAt(i)) {
        digit.textContent = text.charAt(i);
        if (!reduceMotion) {
          digit.classList.remove('is-turning');
          void digit.offsetWidth;
          digit.classList.add('is-turning');
        }
      }
    }
  };

  Clock.prototype.tick = function () {
    var remaining = this.remaining();
    var total = Math.floor(remaining / 1000);

    if (remaining > this.prevRemaining + 2000) this.rollover();
    this.prevRemaining = remaining;

    var d = Math.floor(total / 86400);
    var h = Math.floor((total % 86400) / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;

    this.setUnit(this.units.d, d);
    this.setUnit(this.units.h, h);
    this.setUnit(this.units.m, m);
    this.setUnit(this.units.s, s);

    var minuteKey = d * 1440 + h * 60 + m;
    if (minuteKey !== this.lastMinute) {
      this.lastMinute = minuteKey;
      var label = d + ' days, ' + h + ' hours and ' + m + ' minutes remain';
      if (this.face) this.face.setAttribute('aria-label', label);
      if (this.readout) this.readout.textContent = label;
    }

    var delay = 1000 - (Date.now() % 1000) + 8;
    this.timer = window.setTimeout(this.tick, delay);
  };

  Clock.prototype.rollover = function () {
    var el = this.el;
    el.classList.remove('is-rolling');
    void el.offsetWidth;
    el.classList.add('is-rolling');
    window.setTimeout(function () { el.classList.remove('is-rolling'); }, 2400);
  };

  Clock.prototype.onVisibility = function () {
    if (document.hidden) {
      window.clearTimeout(this.timer);
      this.timer = null;
    } else if (!this.timer) {
      this.tick();
    }
  };

  Clock.prototype.summonEmbers = function () {
    var field = this.el.querySelector('[data-embers-field]');
    if (!field) return;
    var el = this.el;
    var count = window.innerWidth < 750 ? 16 : 30;
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var ember = document.createElement('span');
      ember.className = 'ember';
      var size = (Math.random() * 3.2 + 1.6).toFixed(1);
      ember.style.setProperty('--left', (Math.random() * 100).toFixed(2) + '%');
      ember.style.setProperty('--size', size + 'px');
      ember.style.setProperty('--dur', (Math.random() * 9 + 8).toFixed(1) + 's');
      ember.style.setProperty('--delay', (-Math.random() * 16).toFixed(1) + 's');
      ember.style.setProperty('--sway', ((Math.random() - 0.5) * 120).toFixed(0) + 'px');
      ember.style.setProperty('--peak', (Math.random() * 0.5 + 0.45).toFixed(2));
      fragment.appendChild(ember);
    }
    field.appendChild(fragment);
    function setRise() { el.style.setProperty('--rise', '-' + (el.offsetHeight + 60) + 'px'); }
    setRise();
    if ('ResizeObserver' in window) {
      this.resizer = new ResizeObserver(setRise);
      this.resizer.observe(el);
    } else {
      window.addEventListener('resize', setRise);
    }
  };

  Clock.prototype.destroy = function () {
    window.clearTimeout(this.timer);
    this.timer = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.resizer) this.resizer.disconnect();
  };

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-eternal-clock]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].__eternalClock) {
        nodes[i].__eternalClock = new Clock(nodes[i]);
        instances.push(nodes[i].__eternalClock);
      }
    }
  }

  function unload(scope) {
    var nodes = scope.querySelectorAll('[data-eternal-clock]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__eternalClock) {
        nodes[i].__eternalClock.destroy();
        nodes[i].__eternalClock = null;
      }
    }
  }

  window.KryptikEternalClock = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unload(event.target); });
})();
